import { useEffect, useMemo, useRef, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import {
    Heading,
    TypographyColor,
    TypographyType,
    ButtonVariant,
    ButtonTheme,
    Button,
    ResizableContainer,
    VirtualizedTable,
    InfoPanel,
    TableMenu
} from "../../ui-elements";
import {
    recommendedRegionsState,
    geoState,
    selPOICategories,
    selectedBrands,
    hoveredBrandState,
    focusedPoiState,
    selectedTabState,
    checkboxState,
    selectedGeoNameState,
    geoJsonState,
    compareListState,
    selectedYearState,
    ptypeState,
    selectedGeoDropdownState,
    currencies,
    userCurrencyState,
    topFiveRegionsState,
    dynamicPoiTogglesState,
    filtersState,
    markerFilterDataState,
    markerFiltersPayloadState,
    selMarkerCategoriesState,
} from "../../states";
import { useMapsService } from '../../services';
import { useCompareService } from "../../services/Compare.service";
import { useHandleError, buildColumns, checkIsBrandSelected, buildRecommendedColumns, subGeoLevel, formatLineChartYAxisLabel, cleanFilterTree } from '../../helpers';
import { POI_TABLE_INFO, TABLE_STYLE_CONFIG, TABLE_VIEW_TYPES, RouteConstants } from "../../constants";
import equal from "fast-deep-equal";
import { TableSkeletonLoader } from "./TableSkeleton";
import { getMarkerColor } from "./markerUtils";

interface RegionTabularViewProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    onClickRequestPoI: (item?: any, purpose?: string) => void;
    mbpFilter: any;
    selCatchmentType: any;
    setTableClickedPoi?: React.Dispatch<React.SetStateAction<any>>;
    setTableClickedMbp?: React.Dispatch<React.SetStateAction<any>>;
    onTableDataChange?: (data: Partial<{
        poiHeaders: any[];
        mbpHeaders: any[];
        recommendedRegionsTableData: { tableHeaderData: any[]; topRegions: any[] };
    }>) => void;
}

export const RegionTabularView = ({
    open,
    setOpen,
    onClickRequestPoI,
    mbpFilter,
    selCatchmentType,
    setTableClickedPoi,
    setTableClickedMbp,
    onTableDataChange,
}: RegionTabularViewProps) => {
    const navigate = useNavigate();
    const mapService = useMapsService();
    const compareService = useCompareService();
    const { getErrorMsg } = useHandleError();
    const [searchParams] = useSearchParams();
    const catchment = searchParams.get('catchment');
    const selCategoryList = useRecoilValue(selPOICategories);
    const [payload, setPayload] = useRecoilState(recommendedRegionsState);
    const selectedGeo = useRecoilValue(geoState);
    const setSelectedGeo = useSetRecoilState(geoState);
    const selBrands = useRecoilValue(selectedBrands);
    const selectedTab = useRecoilValue(selectedTabState);
    const isChecked = useRecoilValue(checkboxState);
    const geoJSON = useRecoilValue(geoJsonState);
    const [selectedGeoName, setSelectedGeoName] = useRecoilState(selectedGeoNameState);
    const setHoveredBrand = useSetRecoilState<any>(hoveredBrandState);
    const [focusedPoi, setFocusedPoi] = useRecoilState(focusedPoiState);
    const compareList = useRecoilValue(compareListState);
    const [selectedYear, setSelectedYear] = useRecoilState(selectedYearState);
    const setTopFiveRegions = useSetRecoilState(topFiveRegionsState);
    const dynamicPoiToggles = useRecoilValue(dynamicPoiTogglesState);
    const filters = useRecoilValue<any[]>(filtersState);
    const markerFiltersPayload = useRecoilValue<any[]>(markerFiltersPayloadState);
    const selMarkerCategories = useRecoilValue(selMarkerCategoriesState);
    const markerFilterData = useRecoilValue(markerFilterDataState);
    const [tableData, setTableData] = useState<{ tableHeaderData: any[]; topRegions: any[] }>({ tableHeaderData: [], topRegions: [] });
    const [topMbps, setTopMbps] = useState<{ tableHeaderData: any[]; tableData: any[], detail?: string, has_poi?: boolean }>({ tableHeaderData: [], tableData: [], has_poi: true });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [menuOpenRow, setMenuOpenRow] = useState<number | null>(null);
    const [mbpMenuOpenRow, setMbpMenuOpenRow] = useState<number | null>(null);
    const [selectedRowGeoId, setSelectedRowGeoId] = useState<number | null>(null);
    const [tableViewType, setTableViewType] = useState<'region' | 'poi' | 'mbp'>('region');
    const [regionSorting, setRegionSorting] = useState<SortingState>([]);
    const [poiSorting, setPoiSorting] = useState<SortingState>([]);
    const [mbpSorting, setMbpSorting] = useState<SortingState>([]);
    const prevPayloadRef = useRef();
    const abortControllerRef = useRef<AbortController | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const poiAbortRef = useRef<AbortController | null>(null);
    const pType = useRecoilValue(ptypeState);
    const selectedGeoDropdown = useRecoilValue(selectedGeoDropdownState);
    const [data,setData] = useState({})
    const selCurrency = useRecoilValue(userCurrencyState);
    

    const geoCode = useMemo(() => {
        if (selectedGeo.village) return selectedGeo.village;
        if (selectedGeo.sub_district) return selectedGeo.sub_district;
        if (selectedGeo.district) return selectedGeo.district;
        if (selectedGeo.state) return selectedGeo.state;
        if (selectedGeo.country) return selectedGeo.country === '1' ? 'EW-IN-IND-22-0' : selectedGeo.country;
        return null;
    }, [catchment, selectedGeo]);

    // Determine current compare list based on table header
    // const currCompareList = useMemo(() => {
    //     const headerLabel = tableData?.tableHeaderData?.[1]?.header;
    //     if (headerLabel === 'District') {
    //         return new Set(compareList?.districts?.map((i: any) => i.geoId));
    //     } else if (headerLabel === 'Pincode') {
    //         return new Set(compareList?.pincodes?.map((i: any) => i.geoId));
    //     } else if (headerLabel === 'Village') {
    //         return new Set(compareList?.villages?.map((i: any) => i.geoId));
    //     }
    //     return new Set(compareList?.subdistricts?.map((i: any) => i.geoId));
    // }, [tableData?.tableHeaderData, compareList]);

     const currCompareList = useMemo(() => {
        const headerLabel = tableData?.tableHeaderData?.[1]?.header;
        if (headerLabel !== 'Catchment') {
            if (headerLabel === 'District') {
                return new Set(compareList?.districts?.map((i: any) => i.geoId));
            } else if (headerLabel === 'Pincode') {
                return new Set(compareList?.pincodes?.map((i: any) => i.geoId));
            } else if (headerLabel === 'Village') {
                return new Set(compareList?.villages?.map((i: any) => i.geoId));
            } else {
                return new Set(compareList?.subdistricts?.map((i: any) => i.geoId));
            }
        } else if (pType === 'Branch') {
            return new Set(compareList?.Branch?.map((i: any) => i.geoId));
        } else if (pType === 'Partner') {
            return new Set(compareList?.Partner?.map((i: any) => i.geoId));
        }
        return new Set(compareList?.RelationshipManager?.map((i: any) => i.geoId));
    }, [tableData?.tableHeaderData, compareList, pType]);

    // Reset table view type when navigating away from sub-district level or when catchments is enabled
    useEffect(() => {
        // Auto-switch to region if MBP is disabled
        if (!isChecked?.MBP && tableViewType === 'mbp') {
            setTableViewType('region');
            return;
        }
        // Auto-switch to region if all marker checkboxes (POI + non-POI) are disabled
        const allMarkersOff = !isChecked?.poimarker && Object.values(dynamicPoiToggles).every(v => !v);
        if (allMarkersOff && tableViewType === 'poi') {
            setTableViewType('region');
            return;
        }

        if (!selectedGeo.village || isChecked?.catchments) {
            setTableViewType('region');
        } else if (!allMarkersOff) {
            setTableViewType('poi');
        }
    }, [selectedGeo, isChecked?.catchments, isChecked?.poimarker, isChecked?.MBP, dynamicPoiToggles]);

    const getTitleLabel = () => {
        let levelName = '';


        if (isChecked?.catchments && !catchment) {
            return `${selCatchmentType?.title}${selCatchmentType?.title === 'Branch' ? 'es' : 's'} in ${geoJSON?.rootProperties?.geoName || selectedGeoName}`;
        }

        if (catchment || geoCode === selectedGeo.sub_district) {
            if (!isChecked?.adjacency)
                return `Within ${geoJSON?.rootProperties?.geoName || selectedGeoName}`;
            else
                return `Within ${geoJSON?.rootProperties?.geoName || selectedGeoName} and around `;
        }
        else if (selectedGeo.village) levelName = "PoIs";
        else if (selectedGeo?.district) levelName = selectedTab === 5 ? "Pincodes" : "Subdistricts";
        else if (!levelName) levelName = "Districts";

        if(tableViewType=="mbp") levelName="MBPs"
        if (!isChecked.adjacency) return `${levelName} in ${geoJSON?.rootProperties?.geoName || selectedGeoName}`;
        else return `${levelName} in ${geoJSON?.rootProperties?.geoName || selectedGeoName} and around`;
    };

   const getTabTitle = (name:String) => {
        if(name=="MBP" || name== "PoI") return name
        if(!selectedGeo?.state && !catchment) return "District"
         return subGeoLevel(selectedGeo, selectedTab, catchment)?.charAt(0)?.toUpperCase() + subGeoLevel(selectedGeo, selectedTab, catchment)?.slice(1).replace("_","-")  
    };

    // Navigation handler for recommended regions
    const navigateTo = (item: any, routeTo: string) => {
        setSelectedGeoName(item?.geoName);

        if (item?.geoLevel === 'catchment') {
            searchParams.set('catchment', item?.geoId);
            navigate({
                pathname: routeTo === "/dashboard" ? RouteConstants.dashboard : RouteConstants.catchments,
                search: `?${searchParams.toString()}`,
            });
            return;
        }

        const params = new URLSearchParams();
        const selGeo: Record<string, any> = {};

        ['country', 'state', 'district', 'sub_district', 'village'].forEach(key => {
            if (key === 'sub_district' && !item['sub_district'] && item['pincode']) {
                params.append(key, item['pincode']);
                selGeo[key] = item['pincode'];
            } else if (item[key] !== undefined && item[key] !== null && item[key] !== "") {
                params.append(key, item[key]);
                selGeo[key] = item[key];
            }
        });

        setSelectedGeo(selGeo);

        setTimeout(() => navigate({
            pathname: routeTo,
            search: `?${params.toString()}`,
        }), 0);
    };

    // Compare action handler
    const handleCompareAction = async (item: any) => {
        // For region items, guard on currCompareList; MBP callers guard per-row before calling
        if (item?.type !== 'mbp' && currCompareList.size >= 9) return;

        const payload = {
            addToCompare: [{
                geoId: item?.type === 'mbp' && item?.catchment_id !== null ? item?.catchment_id : item?.geoId,
                level: item?.type === 'mbp' && item?.catchment_id !== null ? -1 : item.geoLevelNumber,
                ...(item.geoLevel === 'pincode' && { parent_id: (item.district) }),
                pType:  item?.type === 'mbp' && item?.catchment_id !== null ? item?.category : pType
            }]
        };
        await compareService.postCompreList(payload);
    };

    // Delete from compare handler
    const onClickDelete = async (data: any) => {
        const headerLabel = tableData?.tableHeaderData?.[1]?.header;

        const payload = {
            addToCompare: [],
            removeFromCompare: [{
                geoId: data?.type === 'mbp' && data?.catchment_id !== null ? data?.catchment_id : data?.geoId,
                level: data?.type === 'mbp' && data?.catchment_id !== null ? -1 : data.geoLevelNumber,
                ...(data.geoLevel === 'pincode' && { parent_id: (data.district) }),
                pType:  data?.type === 'mbp' && data?.catchment_id !== null ? data?.category : pType
            }]
        };
        await compareService.postCompreList(payload);
    };


    // Menu columns configuration
    const getMenuCols = (item: any) => {
        const baseCols = [
            { id: 'explore_more', label: 'Explore More', icon: '', onClick: (row: any) => navigateTo(row, RouteConstants.dashboard) },
            { id: 'add_compare', label: 'Add to compare', icon: 'add', onClick: (row: any) => handleCompareAction(row), isDisabled: currCompareList.size >= 9 },
            { id: 'remove', label: 'Remove', icon: 'delete', onClick: (row: any) => onClickDelete(row) },
        ];

        // if (item.geoLevel === 'catchment') {
        //     return baseCols.filter(col => col.id !== 'add_compare' && col.id !== 'remove');
        // }

        const itemGeoId = item.geoLevel === 'pincode'
            ? item.pincode
            : item.geoLevel === "village"
                ? item.village || item.geoId
                : item.geoLevel === 'catchment'
                    ? item.geoId
                    : item.sub_district || item.district;

        if (currCompareList.has(itemGeoId)) {
            return baseCols.filter(col => col.id !== 'add_compare');
        }
        return baseCols.filter(col => col.id !== 'remove');
    };

    // POI table helpers
    const checkSelPois = (branchId: string) => {
        return checkIsBrandSelected(branchId, selBrands);
    }

    const highlightPoiInMap = (item: any) => {
        const poiData = { ...item?.original, branchName: item?.original?.companyName, isSelected: false };
        if (setTableClickedPoi) {
            setTableClickedPoi(poiData);
        } else {
            setHoveredBrand(poiData);
            setFocusedPoi({ type: "poi", data: poiData });
        }
    }

    const highlightMbpInMap = (item: any) => {
        const mbpData = { ...item?.original };
        if (setTableClickedMbp) {
            setTableClickedMbp(mbpData);
        } else {
            setFocusedPoi({ type: "mbp", data: mbpData });
        }
    }

    const topPoisPayloadRaw = useMemo(() => {
        const geoId = selectedGeo?.village || catchment || selectedGeo?.sub_district;
        if (!geoId) return null;
        return {
            geoId,
            ...selCategoryList,
            file: false,
            level: "PoI",
            data: mbpFilter?.data || [],
            filters: filters.map(cleanFilterTree),
            marker_filters: markerFiltersPayload.map(cleanFilterTree),
        };
    }, [selectedGeo, selCategoryList, mbpFilter, catchment, filters, markerFiltersPayload]);

    const topPoisPayloadRef = useRef<any>(topPoisPayloadRaw);
    if (!equal(topPoisPayloadRef.current, topPoisPayloadRaw)) {
        topPoisPayloadRef.current = topPoisPayloadRaw;
    }
    const topPoisPayload = topPoisPayloadRef.current;

    const topMbpsPayloadRaw = useMemo(() => {
        if (!geoCode) return null;
        return {
            geoId: catchment || geoCode,
            ...selCategoryList,
            file: false,
            level: "PoI",
            data: mbpFilter?.data || [],
            geoLevelNumber: selectedGeo?.district && !selectedGeo?.sub_district ? selectedTab : null,
            ...dynamicPoiToggles,
            filters: filters.map(cleanFilterTree),
            marker_filters: markerFiltersPayload.map(cleanFilterTree),
            n_layer_category: Object.entries(selMarkerCategories)
                .filter(([key]) => key !== 'poimarker' && key !== 'PoI')
                .flatMap(([layerKey, sel]) => {
                    const rawCats = markerFilterData[layerKey]?.categories ?? [];
                    const cats = rawCats.map((c: any, i: number) => c.key !== undefined ? c : { ...c, key: i });
                    const selectedKeys = new Set(sel.category);
                    return cats
                        .filter((c: any) => selectedKeys.has(c.key))
                        .map((c: any) => ({ key: c.key, name: c.name,layer_id:c.layer_id  }));
                }),
        };
    }, [selectedGeo, selCategoryList, mbpFilter, geoCode, catchment, isChecked?.catchments, selectedTab, dynamicPoiToggles, filters, markerFiltersPayload, selMarkerCategories, markerFilterData]);

    const topMbpsPayloadRef = useRef<any>(topMbpsPayloadRaw);
    if (!equal(topMbpsPayloadRef.current, topMbpsPayloadRaw)) {
        topMbpsPayloadRef.current = topMbpsPayloadRaw;
    }
    const topMbpsPayload = topMbpsPayloadRef.current;

    const { data: combinedQueryData, isError: isCombinedError } = useQuery({
        queryKey: ['poiMbpData', topMbpsPayload, isChecked?.adjacency],
        queryFn: () => {
            poiAbortRef.current?.abort();
            poiAbortRef.current = new AbortController();
            return mapService.getTopPois(topMbpsPayload, poiAbortRef.current.signal);
        },
        enabled: !!topMbpsPayload,
        staleTime: 5 * 60 * 1000,
    });

    const topPois = useMemo(() => {
        if (isCombinedError || !combinedQueryData) return { tableHeaderData: [], tableData: [], has_poi: true };
        return { ...combinedQueryData.poi, has_poi: combinedQueryData.has_poi, detail: combinedQueryData.detail };
    }, [combinedQueryData, isCombinedError]);

    useEffect(() => {
        if (isCombinedError) {
            setTopMbps({ tableHeaderData: [], tableData: [] });
        } else if (combinedQueryData) {
            setTopMbps({ ...combinedQueryData.mbp, has_mbp: combinedQueryData.has_mbp, detail: combinedQueryData.detail });
            onTableDataChange?.({
                poiHeaders: combinedQueryData.poi?.tableHeaderData ?? [],
                mbpHeaders: combinedQueryData.mbp?.tableHeaderData ?? [],
            });
        }
    }, [combinedQueryData, isCombinedError]);

    // Build columns for POI table
    const poiColumns = useMemo(() => {
        const baseColumns = buildColumns(topPois?.tableHeaderData, checkSelPois);
        return baseColumns.map((col: any) => {
            if (col.accessorKey?.includes('.')) {
                return {
                    ...col,
                    cell: (info: any) => {
                        const row = info.row.original;
                        const val = info.getValue();
                        const [r, g, b] = getMarkerColor(row?.type ?? 'poimarker');
                        return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                <span
                                    className="shrink-0 rounded-full inline-block h-2 w-2"
                                    style={{
                                        backgroundColor: `rgb(${r},${g},${b})`,
                                        border: `1px solid rgb(${Math.round(r * 0.6)},${Math.round(g * 0.6)},${Math.round(b * 0.6)})`,
                                    }}
                                />
                                <span>{val ? val as string : '--'}</span>
                            </span>
                        );
                    },
                };
            }
            return col;
        });
    }, [topPois?.tableHeaderData, selBrands, checkSelPois]);

    const filteredPoiData = useMemo(() => {
        return (topPois?.tableData ?? []).filter((row: any) => {
            const type = row?.type ?? 'poimarker';
            if (type === 'poimarker') return !!isChecked?.poimarker;
            const toggle = dynamicPoiToggles[type];
            return toggle !== false;
        });
    }, [topPois?.tableData, isChecked?.poimarker, dynamicPoiToggles]);

    // Build columns for MBP table
    const mbpColumns = useMemo(() => {
        const baseColumns = buildColumns(topMbps?.tableHeaderData, checkSelPois);

        if (baseColumns.length > 0) {
            baseColumns.push({
                id: 'actions',
                accessorKey: 'actions',
                header: '',
                enableSorting: false,
                size: 50,
                cell: (info: any) => {
                    const row = info.row.original;
                    const rowIndex = info.row.index;

                    const catchmentId = row?.catchment_id ?? null;
                    const noCatchment = catchmentId == null;
                    const category = row?.category?.split(' ').join('');
                    const currentCompareList = new Set(compareList?.[category]?.map((i: any) => i.geoId));
                    const isAlreadyAdded = !noCatchment && currentCompareList.has(catchmentId);
                    const mbpAsGeo = { ...row, geoId: catchmentId, catchment_id: catchmentId };

                    const exploreMbp = !noCatchment ? {
                        id: 'explore_more',
                        label: 'Explore More',
                        icon: '',
                        onClick: (r: any) => {
                            const params = new URLSearchParams(searchParams);
                            params.set('catchment', r.catchment_id);
                            navigate({ pathname: RouteConstants.dashboard, search: `?${params.toString()}` });
                        },
                    } : null;

                    const cols = isAlreadyAdded
                        ? [{ id: 'remove', label: 'Remove', icon: 'delete', onClick: (r: any) => onClickDelete(r) }, ...(exploreMbp ? [exploreMbp] : [])]
                        : [{ id: 'add_compare', label: 'Add to compare', icon: 'add', onClick: (r: any) => handleCompareAction(r), isDisabled: noCatchment ? true : currentCompareList.size >= 9 }, ...(exploreMbp ? [exploreMbp] : [])];

                    return (
                        <div className="d-flex align-items-center justify-content-center h-100">
                            <TableMenu
                                row={mbpAsGeo}
                                cols={cols}
                                menuRef={menuRef}
                                onMenuClick={(flag: boolean | undefined) =>
                                    setMbpMenuOpenRow(flag ? null : mbpMenuOpenRow === rowIndex ? null : rowIndex)
                                }
                                index={rowIndex}
                                selRow={mbpMenuOpenRow}
                                showTooltip={new Map([['add_compare', ((noCatchment || currentCompareList.size >= 9) && mbpMenuOpenRow === rowIndex)]])}
                                tooltipText={noCatchment ? 'No catchment available to compare.' : 'You have reached the limit of 9 regions. Remove some to add more.'}
                            />
                        </div>
                    );
                }
            });
        }

        return baseColumns;
    }, [topMbps?.tableHeaderData, selBrands, checkSelPois, mbpMenuOpenRow, compareList]);

    // Build columns for recommended regions table
    const recommendedColumns = useMemo(() => {
        const baseColumns = buildRecommendedColumns(tableData?.tableHeaderData || [], {
            showPresence: isChecked.MBP && payload?.mbpdata?.length > 0,
            selectedYear,
            onYearChange: setSelectedYear,
            onGeoNameClick: (row) => navigateTo(row, RouteConstants.root),
            onRequestPoI: (row) => onClickRequestPoI(row, 'PoI coverage'),
            sorting: regionSorting
        });

        // Add action column for menu
        if (baseColumns.length > 0) {
            baseColumns.push({
                id: 'actions',
                accessorKey: 'actions',
                header: '',
                enableSorting: false,
                size: 50,
                cell: (info: any) => {
                    const row = info.row.original;
                    const rowIndex = info.row.index;

                    return (
                        <div className="d-flex align-items-center justify-content-center h-100">
                            <TableMenu
                                row={row}
                                cols={getMenuCols(row)}
                                menuRef={menuRef}
                                onMenuClick={(flag: boolean | undefined) =>
                                    setMenuOpenRow(flag ? null : menuOpenRow === rowIndex ? null : rowIndex)
                                }
                                index={rowIndex}
                                selRow={menuOpenRow}
                                showTooltip={new Map([['add_compare', (currCompareList.size >= 9 && menuOpenRow === rowIndex)]])}
                                tooltipText='You have reached the limit of 9 regions. Remove some to add more.'
                            />
                        </div>
                    );
                }
            });
        }

        return baseColumns;
    }, [
        tableData?.tableHeaderData,
        isChecked.MBP,
        payload?.mbpdata,
        selectedYear,
        menuOpenRow,
        currCompareList,
        regionSorting
    ]);

    // Sort recommended regions data - filtered items at the end
    const sortedRegionsData = useMemo(() => {
        if (!tableData?.topRegions || !Array.isArray(tableData.topRegions)) {
            return [];
        }

        return [...tableData.topRegions].sort((a, b) => {
            const aFiltered = a.rankData?.filteredOut === true;
            const bFiltered = b.rankData?.filteredOut === true;

            if (aFiltered && !bFiltered) return 1;
            if (!aFiltered && bFiltered) return -1;
            return 0;
        });
    }, [tableData?.topRegions]);

    // Row style for recommended regions
    // const getRegionsRowStyle = (row: any) => {
    //     const isFiltered = row.original?.rankData?.filteredOut;
    //     if (isFiltered) {
    //         return { backgroundColor: 'rgba(0, 0, 0, 0.04)' };
    //     }
    //     return { backgroundColor: '#FFFFFF' };
    // };

    // Row style for POI table
    const getPoiRowStyle = (row: any) => {
        const isSelected = checkSelPois(row.original?.branchId);
        if (isSelected) return { backgroundColor: 'rgba(229, 235, 254, 0.5)' };
        return { backgroundColor: '#FFFFFF' };
    };

    // Row style for MBP table
    const getMbpRowStyle = (row: any) => {
        const isSelected = checkSelPois(row.original?.branchId);
        if (isSelected) return { backgroundColor: 'rgba(229, 235, 254, 0.5)' };
        return { backgroundColor: '#FFFFFF' };
    };

    const downloadCsvData = async () => {
        if (payload && payload?.mbpdata?.length >= 0 && payload.category.length > 0) {
            const updatedPayload = {
                ...payload,
                poiColumnName: `PoI growth (${payload?.poigrowthyear} ${payload?.poigrowthyear > 1 ? 'years' : 'year'})`,
                file: true,
                ...(selectedGeo?.district && { sublevel: selectedTab })

            }

            // Fix stale geoId issue - use topPoisPayload.geoId if payload.geoId is in invalid range
            if (selectedGeoDropdown?.geotype === "catchment" && topPoisPayload?.geoId) {
                updatedPayload.geoId = topPoisPayload.geoId;
            }

            if (!selectedGeo?.district) delete updatedPayload.sublevel;

            try {
                let response;
                const isPoiView = ((catchment || selectedGeo?.village === geoCode || selectedGeo?.sub_district === geoCode) && tableViewType === 'poi')
                const isMbpView = tableViewType === 'mbp';

                if (isMbpView) {
                    response = await mapService.getTopPois({ ...topMbpsPayload, file: true, filetype:"mbp", ...dynamicPoiToggles, poimarker: isChecked?.poimarker });
                } else if (isPoiView) {
                    response = await mapService.getTopPois({ ...topPoisPayload, file: true, filetype:"poi", ...dynamicPoiToggles, poimarker: isChecked?.poimarker });
                } else {
                    response = await mapService.getRecommendedRegions(updatedPayload);
                }

                if (response) {
                    const blob = new Blob([response], { type: "text/csv" });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;

                    const today = new Date();
                    const mm = String(today.getMonth() + 1).padStart(2, "0");
                    const dd = String(today.getDate()).padStart(2, "0");
                    const yyyy = today.getFullYear();
                    const formattedDate = `${mm}${dd}${yyyy}`;

                    const fileName = `EPICIntelligence_${getTitleLabel().replace(/\s+/g, '_').toLowerCase()}_${formattedDate}.csv`;

                    link.setAttribute("download", fileName);
                    document.body.appendChild(link);
                    link.click();

                    link.remove();
                    window.URL.revokeObjectURL(url);
                }
            }
            catch (error) {
                getErrorMsg(error);
            }
        }
    }

    useEffect(() => {
        setPayload((prev: any) => ({
            ...prev,
            poigrowthyear: selectedYear
        }));
    }, [selectedYear]);

    useEffect(() => {
        if (!equal(prevPayloadRef.current, payload) && (!selectedGeo.village || catchment)) {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const fetchRecommendedTable = async (recommendedRegionsPayload: any) => {
                if(selectedGeoDropdown?.geotype === "catchment") return

                const abortController = new AbortController();
                abortControllerRef.current = abortController;

                setTableData({ tableHeaderData: [], topRegions: [] });
                setIsLoading(true);
                try {
                    const payload = { ...recommendedRegionsPayload };
                    if (!selectedGeo?.district) delete payload.sublevel;
                    else payload.sublevel = selectedTab;

                    const res = await mapService.getRecommendedRegions(payload, abortController.signal);

                    if (!abortController.signal.aborted && res) {
                        setTableData(res);
                        onTableDataChange?.({ recommendedRegionsTableData: res });
                    }
                } catch (error: any) {
                    if (error.name !== 'AbortError') {
                        setTableData({ tableHeaderData: [], topRegions: [] });
                    }
                } finally {
                    if (!abortController.signal.aborted) {
                        setIsLoading(false);
                    }
                }
            }

            if (payload && payload?.category?.length > 0) {
                fetchRecommendedTable(payload);
            }
            prevPayloadRef.current = payload;
        }
    }, [payload, isChecked?.adjacency, selectedGeo, selectedYear]);

    // Update top 5 regions whenever tableData changes
    useEffect(() => {
        if (tableData?.topRegions && Array.isArray(tableData.topRegions)) {
            const topFive = tableData.topRegions
                .filter(region => region?.rankData?.rank != null)
                .slice(0, 5);
            setTopFiveRegions(topFive);
        } else {
            setTopFiveRegions([]);
        }
    }, [tableData, setTopFiveRegions]);

    const actions = [
        {
            icon: "download",
            onClick: downloadCsvData,
        },
        {
            icon: "cancel",
            onClick: () => setOpen(!open),
        },
    ];

        const catchmentId = searchParams.get('catchment');
        const isCatchmentView = !!catchmentId;

    // Render recommended regions table
    const renderRecommendedRegionsTable = () => {

        return (
            <VirtualizedTable
                data={sortedRegionsData}
                columns={recommendedColumns}
                primaryKey="geoId"
                tableConfig={TABLE_STYLE_CONFIG.RECOMMENDED_REGIONS}
                emptyPlaceholder={isChecked?.catchments ? "Choose another region or presence type to view catchments." : ''}
                emptyPlaceholderTitle={isChecked?.catchments ? "No catchments in this region." : ''}
                headerCellStyle={{ background: '#F8F8F8' }}
                rowStyle={{ backgroundColor: '#FFFFFF' }}
                selItem={selectedRowGeoId ?? undefined}
                sorting={regionSorting}
                onSortingChange={setRegionSorting}
                // onRowClick={(row) => setSelectedRowGeoId(row.original?.geoId)}
            />
        );
    };

    // Render POI table
    const renderPoiTable = () => {
        return (
            <VirtualizedTable
                data={filteredPoiData}
                columns={poiColumns}
                primaryKey="branchId"
                tableConfig={TABLE_STYLE_CONFIG.TOP_POIS}
                emptyPlaceholder={topPois?.detail}
                emptyPlaceholderTitle={topPois?.has_poi ? 'Work in Progress' : undefined}
                headerCellStyle={{ background: '#F8F8F8' }}
                rowStyle={getPoiRowStyle}
                selItem={focusedPoi?.data?.branchId}
                onRowClick={highlightPoiInMap}
                sorting={poiSorting}
                onSortingChange={setPoiSorting}
            />
        );
    };

    // Render MBP table
    const renderMbpTable = () => {
        return (
            <VirtualizedTable
                data={topMbps?.tableData}
                columns={mbpColumns}
                primaryKey="unique_key"
                tableConfig={TABLE_STYLE_CONFIG.TOP_MBPS}
                emptyPlaceholder={"NO MBP present"}
                emptyPlaceholderTitle={topMbps?.has_poi ? 'Work in Progress' : undefined}
                headerCellStyle={{ background: '#F8F8F8' }}
                rowStyle={getMbpRowStyle}
                selItem={focusedPoi?.type === 'mbp' ? focusedPoi?.data?.unique_key : ""}
                onRowClick={highlightMbpInMap}
                sorting={mbpSorting}
                onSortingChange={setMbpSorting}
            />
        );
    };

    return (
        <div className="position-absolute left-0 bottom-0 w-100 z-index-21">
            {open && (
                <ResizableContainer
                    defaultHeight={window?.innerHeight * 0.40}
                    minHeight={window?.innerHeight * 0.40}
                    maxHeight={window?.innerHeight * 0.75}
                    enableResize={{ top: true }}
                    className="bg-white w-100 border-top d-flex flex-column overflow-hidden">
                    <div className="d-flex flex-row w-100 justify-content-between align-items-center padding-left-right-4 h-auto">
                        <div className="d-flex justify-content-start align-items-center gap-2 p-0 m-0">
                            <Heading
                                title={getTitleLabel()}
                                type={TypographyType.h3}
                                colour={TypographyColor.primary}
                                classname="m-0 p-0"
                            />
                            {((selectedGeo.village || (selectedGeo.sub_district && tableViewType === 'poi') || (isCatchmentView && tableViewType === 'poi'))) && <InfoPanel text={POI_TABLE_INFO} classname='fs-14 icon-gray-0' />}
                            {(selectedGeo.village || (selectedGeo.sub_district && tableViewType === 'poi') || (isCatchmentView && tableViewType === 'poi')) && (tableViewType==="poi" && topPois?.tableData?.length === 0 && topPois?.detail) &&
                                <Button
                                    theme={ButtonTheme.link}
                                    variant={ButtonVariant.transparent}
                                    type='button'
                                    classname="m-0 p-0 fs-14"
                                    onClick={() => onClickRequestPoI(undefined, !topPois?.has_poi ? 'PoI coverage' : 'PoI data')}
                                >
                                    {!topPois?.has_poi ? `Request PoI Coverage` : 'Request to Download'}
                                </Button>
                            }
                            {/* {tableViewType === 'mbp' && (topMbps?.tableData?.length === 0 && topMbps?.detail) &&
                                <Button
                                    theme={ButtonTheme.link}
                                    variant={ButtonVariant.transparent}
                                    type='button'
                                    classname="m-0 p-0 fs-14"
                                    onClick={() => onClickRequestPoI(undefined, !topMbps?.has_poi ? 'PoI coverage' : 'PoI data')}
                                >
                                    {!topMbps?.has_poi ? `Request PoI Coverage` : 'Request to Download'}
                                </Button>
                            } */}
                        </div>
                        <div className="d-flex flex-row align-items-center gap-2 padding-bottom-5 padding-top-3">
                            {actions.map(action => {
                                const isPoiView = selectedGeo.village || (selectedGeo.sub_district && !selectedGeo.village && tableViewType === 'poi');
                                const isMbpView = tableViewType === 'mbp';
                                if (action.icon === 'download' && isPoiView && topPois?.tableData?.length === 0 && topPois?.detail) return null;
                                if (action.icon === 'download' && isMbpView && topMbps?.tableData?.length === 0 ) return null;
                                return (
                                    <Button
                                        key={action.icon}
                                        theme={ButtonTheme.primary}
                                        variant={ButtonVariant.transparent}
                                        type='button'
                                        classname='dashboard-icon-wrapper d-flex justify-content-center align-items-center'
                                        onClick={action.onClick}
                                    >
                                        <span className="material-symbols-outlined fs-24">
                                            {action.icon}
                                        </span>
                                    </Button>
                                )
                            })}
                        </div>
                    </div>
                    {/* Tab switch - show MBP from nation level, POI from sub-district level */}
                    {(!isChecked?.catchments || catchment) && (
                        <div className="d-flex align-items-center gap-1 ms-3">
                            {TABLE_VIEW_TYPES.map((viewType) => {
                                // POI tab only visible from subdistrict level onwards or in catchment view
                                const isPoiTab = viewType.key === 'poi';
                                const isMbpTab = viewType.key === 'mbp';
                                const isRegionTab = viewType.key === 'region';
                                const isPoiHidden = isPoiTab && (!selectedGeo.sub_district && !catchment); // Hide POI until subdistrict level or catchment
                                const isRegionHidden = isRegionTab && selectedGeo?.village && !catchment; // Hide Region tab at village level (except in catchment view)
                                const isPoiDisabled = isPoiTab && !isChecked?.poimarker && Object.values(dynamicPoiToggles).every(v => !v);
                                const isMbpDisabled = isMbpTab && !isChecked?.MBP;
                                const isDisabled = isPoiDisabled || isMbpDisabled;

                                // Get count for each tab
                                const getCount = () => {
                                    if (viewType.key === 'poi') return filteredPoiData.length;
                                    if (viewType.key === 'mbp') return topMbps?.tableData?.length || 0;
                                    return tableData?.topRegions?.length || 0;
                                };

                                if (isPoiHidden || isRegionHidden) return null;

                                return (
                                    <div key={`parent-${viewType.key}`} className={`d-flex flex-column justify-content-between align-items-center ${!isDisabled ? 'cursor-pointer' : ''}`} style={isDisabled ? { opacity: 0.5 } : {}}>
                                        <Heading
                                            key={viewType.key}
                                            title={`${getTabTitle(viewType.label)}${!isDisabled ? ` (${formatLineChartYAxisLabel(getCount(),selCurrency)})` : ''}`}
                                            type={TypographyType.h5}
                                            colour={tableViewType === viewType.key ? TypographyColor.primary : TypographyColor.secondary}
                                            classname='text-center text-truncate padding-2 '
                                            onClick={!isDisabled ? () => setTableViewType(viewType.key as 'region' | 'poi' | 'mbp') : undefined}
                                        />
                                        <hr className={`m-0 w-100 ${tableViewType === viewType.key ? 'selected-cat' : 'non-selected-cat'}`} />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div className="h-100">
                        {isLoading ? (
                            <TableSkeletonLoader rows={8} cols={6} />
                        ) : (
                            tableViewType === 'mbp'
                                ? renderMbpTable()
                                : tableViewType === 'poi'
                                    ? renderPoiTable()
                                    : renderRecommendedRegionsTable()
                        )}
                    </div>
                </ResizableContainer>
            )}
        </div>
    );
}
